import React, {
  useCallback,
  useState,
  useMemo,
  memo,
}                               from 'react';
import { RouteComponentProps }  from 'react-router-dom';
import {
  IVinDecodeHistoryItem,
  TVinHistory,
  IDashLocationState,
}                               from '@typings/dashboard';
import {
  ISearchParams,
  ISearchFilter,
}                               from '@typings/search';
import {
  IOption,
  ILinkButtonData,
}                               from '@typings/components';
import { EAppPage }             from '@enums/common';
import { EAccountPage }         from '@enums/accountManagement';
import { useFetchVinHistory }   from '@modules/dashboard/queries/dashboardQueries';
import { useFetchAccounts }     from '@modules/accountManagement/queries/accountQueries';
import { parseRecords }         from '@utils/commonFunctions';
import { Loader }               from '@components/Loader/Loader';
import { VinDecodeHistoryPage } from '@modules/dashboard/pages/VinDecodeHistoryPage';

const ROWS_PER_PAGE = 100;

const VinDecodeHistory: React.FC<RouteComponentProps<void, any, IDashLocationState>> = ({
  location,
  history: { push },
}) => {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentCuid, setCurrentCuid] = useState<string>(location.state.cuid ?? '');
  const [cuName, setCuName]           = useState<string>(location.state.cuName ?? '');

  const searchParams = useMemo<ISearchParams>(
    () => {
      const cuidFilter: ISearchFilter = {
        propertyName : 'CUID',
        operator     : 'eq',
        value        : currentCuid,
      };

      return {
        offset : currentPage * ROWS_PER_PAGE,
        limit  : ROWS_PER_PAGE,
        ...(currentCuid && { filters: [cuidFilter] }),
      };
    },
    [currentCuid, currentPage],
  );

  // #region API INTEGRATION
  const {
    data: vinHistoryResponse = {} as TVinHistory,
    isLoading,
  } = useFetchVinHistory(searchParams);

  const { data: cuList } = useFetchAccounts('');
  // #endregion

  const vinDecodeHistory = useMemo<IVinDecodeHistoryItem[]>(
    () => parseRecords<IVinDecodeHistoryItem>(vinHistoryResponse),
    [vinHistoryResponse],
  );

  const accountsOptions = useMemo<IOption[]>(
    () => {
      if (!cuList) {
        return [];
      }

      const allAccountsOption: IOption = {
        value : '',
        label : 'All Accounts',
      };

      const cuOptions: IOption[] = cuList.map(({ cuid, name }) => ({
        value : cuid,
        label : name,
      }));

      return [allAccountsOption, ...cuOptions];
    },
    [cuList],
  );

  const navLinksData = useMemo<ILinkButtonData[]>(
    () => {
      if (currentCuid && cuName) {
        const accountSearchLinkData: ILinkButtonData = {
          label   : 'Return to Account Management',
          onClick : (): void => push(`${EAppPage.AccountManagement}/${EAccountPage.Search}`),
        };

        const accountInfoLinkData: ILinkButtonData = {
          label   : `Return to ${cuName}`,
          onClick : (): void => push(`${EAppPage.AccountManagement}/${currentCuid}/${EAccountPage.Edit}`),
        };

        return [accountSearchLinkData, accountInfoLinkData];
      }

      const dashboardLinkData: ILinkButtonData = {
        label   : 'Return to Dashboard',
        onClick : (): void => push(EAppPage.Dashboard),
      };

      return [dashboardLinkData];
    },
    [cuName, currentCuid, push],
  );

  const onChangePage = useCallback(
    (pageNumber: number): void => {
      setCurrentPage(pageNumber);
    },
    [],
  );

  const onCuidChange = useCallback(
    (option: IOption): void => {
      setCurrentCuid(option.value as string);
      setCuName(option.label as string);
    },
    [],
  );

  if (!cuList) {
    return <Loader />;
  }

  return (
    <VinDecodeHistoryPage
      rowsPerPage                = {ROWS_PER_PAGE}
      currentPage                = {currentPage}
      vinDecodeHistory           = {vinDecodeHistory}
      totalVinDecodeHistoryCount = {vinHistoryResponse.totalRecordsFound}
      isLoading                  = {isLoading}
      curCuid                    = {currentCuid}
      accountsOptions            = {accountsOptions}
      isFilterDisabled           = {!!(location.state.cuid && location.state.cuName)}
      navLinksData               = {navLinksData}
      onCuidChange               = {onCuidChange}
      handleChangePage           = {onChangePage}
    />
  );
};

export const VinDecodeHistoryContainer = memo(VinDecodeHistory);
